import React from 'react';

// Typography component for consistent text styling
export const Typography = ({ 
  variant = 'body', 
  children, 
  className = '', 
  as: Component = 'p',
  ...props 
}) => {
  const variants = {
    // Headings
    h1: 'text-4xl font-bold text-text-primary leading-tight',
    h2: 'text-3xl font-bold text-text-primary leading-tight',
    h3: 'text-2xl font-semibold text-text-primary leading-snug',
    h4: 'text-xl font-semibold text-text-primary leading-snug',
    h5: 'text-lg font-medium text-text-primary leading-snug',
    h6: 'text-base font-medium text-text-primary leading-snug',
    
    // Body text
    body: 'text-base text-text-primary leading-relaxed',
    'body-large': 'text-lg text-text-primary leading-relaxed',
    'body-small': 'text-sm text-text-secondary leading-relaxed',
    strong: 'font-semibold text-text-primary leading-normal',
    
    // Specialized text
    caption: 'text-xs text-text-muted leading-normal',
    label: 'text-sm font-medium text-text-primary leading-normal',
    'label-small': 'text-xs font-medium text-text-secondary leading-normal',
    
    // Interactive text
    link: 'text-base text-primary hover:text-primary-dark underline-offset-4 hover:underline cursor-pointer',
    'link-small': 'text-sm text-primary hover:text-primary-dark underline-offset-4 hover:underline cursor-pointer',
    
    // Status text
    success: 'text-sm font-medium text-success-dark',
    warning: 'text-sm font-medium text-warning-dark',
    error: 'text-sm font-medium text-danger-dark',
    info: 'text-sm font-medium text-text-primary',
    
    // Muted text
    muted: 'text-sm text-text-muted leading-normal',
    'muted-small': 'text-xs text-text-muted leading-normal'
  };

  // Determine the component to render based on variant
  const getComponent = () => {
    if (Component !== 'p') return Component;
    
    switch (variant) {
      case 'h1': return 'h1';
      case 'h2': return 'h2';
      case 'h3': return 'h3';
      case 'h4': return 'h4';
      case 'h5': return 'h5';
      case 'h6': return 'h6';
      case 'strong': return 'span';
      case 'link':
      case 'link-small': return 'a';
      default: return 'p';
    }
  };

  const ComponentToRender = getComponent();

  return (
    <ComponentToRender
      className={`${variants[variant]} ${className}`}
      {...props}
    >
      {children}
    </ComponentToRender>
  );
};

// Convenience components for common typography patterns
export const Heading = ({ level = 1, children, className = '', ...props }) => {
  const variant = `h${level}`;
  return (
    <Typography variant={variant} className={className} {...props}>
      {children}
    </Typography>
  );
};

export const Text = ({ children, className = '', ...props }) => (
  <Typography variant="body" className={className} {...props}>
    {children}
  </Typography>
);

export const Caption = ({ children, className = '', ...props }) => (
  <Typography variant="caption" className={className} {...props}>
    {children}
  </Typography>
);

export const Label = ({ children, className = '', ...props }) => (
  <Typography variant="label" className={className} {...props}>
    {children}
  </Typography>
);

export default Typography;
